import { useRef, useState } from "react";
import { ImageIcon, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg, mediaSrc } from "../../lib/api";

export default function MediaPicker({ id, value, onChange, kind = "image", hint }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState("");
  const file = useRef(null);
  const openPicker = () => { setOpen(true); api.get("/admin/media").then((r) => setItems(r.data.filter((m) => m.kind === kind))); };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large: max 5 MB"); return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); onChange((await api.post("/admin/media/upload", fd)).data.url); toast.success("Uploaded"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {value && kind === "image" && (broken === value
          ? <span className="shrink-0 rounded border border-red-300 bg-red-50 px-2 py-3 text-xs font-semibold text-red-800" role="alert" data-testid={`media-missing-${id}`}>Image missing – please re-upload</span>
          : <img src={mediaSrc(value)} alt="" onError={() => setBroken(value)} className="h-14 max-w-[10rem] shrink-0 rounded border bg-slate-50 object-contain" data-testid={`media-preview-${id}`} />)}
        <input id={id} className="field-input min-w-0 flex-1 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`media-input-${id}`} />
        {kind === "image" && <>
          <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid={`media-file-${id}`} />
          <button type="button" className="btn-outline btn-sm shrink-0" disabled={busy} onClick={() => file.current.click()} data-testid={`media-upload-${id}`}>
            <Upload size={16} aria-hidden="true" /> {busy ? "Uploading…" : value ? "Replace" : "Upload"}</button></>}
        <button type="button" className="btn-navy btn-sm shrink-0" onClick={openPicker} data-testid={`media-choose-${id}`}><ImageIcon size={16} aria-hidden="true" /> Library</button>
        {value && <button type="button" className="btn-outline btn-sm shrink-0" onClick={() => onChange("")} aria-label="Remove image" data-testid={`media-remove-${id}`}><Trash2 size={16} aria-hidden="true" /></button>}
      </div>
      {hint && <p className="mt-1 text-xs text-slate-600">{hint}</p>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="media-picker-dialog">
          <DialogHeader><DialogTitle>Choose from media library</DialogTitle></DialogHeader>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {items.map((m) => (
              <li key={m.id}>
                <button type="button" className="w-full rounded border border-slate-200 p-1 text-left hover:border-navy" onClick={() => { onChange(m.url); setOpen(false); }} data-testid={`media-pick-${m.id}`}>
                  {m.kind === "image" ? <img src={mediaSrc(m.url)} alt={m.alt || ""} className="aspect-video w-full rounded object-cover" /> : <span className="block p-6 text-center text-sm">{m.filename}</span>}
                  <span className="mt-1 block truncate text-xs">{m.label || m.filename}</span>
                </button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
